import React, { useState } from 'react';
import { useTranslation } from 'localization';
import GlyphAtom from '../../../atoms/Glyph/GlyphAtom';
import IconButtonAtom from '../../../atoms/IconButton/IconButtonAtom';
import { useApiClientsOrNull } from '../../../../context/ApiClientsContext';
import { useSpherePermission } from '../../../../hooks/useSpherePermission';
import type { FactorRosterMember } from '../../../../types/statusFactors';

/**
 * A removal that already happened, reported so the molecule can offer Undo once the row (and this cell)
 * has unmounted after a refetch. `undo` is set for an unlink (re-POST the captured pin); an exclusion is
 * lifted by the molecule through the exclusions route, which needs nothing captured from the row.
 */
export interface RosterRemoval {
  kind: 'excluded' | 'unlinked';
  sphereId: string;
  memberName: string;
  /** The member's `sources[].kind`s at removal time; reported with the exclusion analytics event. */
  viaKinds?: string[];
  undo?: () => Promise<void>;
}

/** A failed removal, reported upward so the molecule can render the alert under the row. */
export interface RosterActionFailure {
  memberSphereId: string;
  memberName: string;
  /** `forbidden` (the route's 403) is not retryable: the caller lacks edit on the aggregate. */
  kind: 'failed' | 'forbidden';
  retry: () => void;
}

export interface ActionsCellProps {
  sphereId: string;
  factorId: string;
  member: FactorRosterMember;
  /** Called after the DELETE succeeds. Both removals fire at once; Undo is offered afterwards, never a dialog before. */
  onRemoved: (removal: RosterRemoval) => void;
  onFailed?: (failure: RosterActionFailure | null) => void;
  /** The editor is read-only: render nothing, as for a viewer. */
  disabled?: boolean;
}

const isForbidden = (error: unknown) => (error as { response?: { status?: number } })?.response?.status === 403;

/**
 * The one removal affordance a member has, named by the server's `removal` (never inferred from
 * `sources`). Empty for viewers, while permission loads, and for hidden members (`removal: null`).
 * The UI gate is advisory — the route's 403 is the enforcement and is surfaced as a non-retryable alert.
 */
const ActionsCell: React.FC<ActionsCellProps> = ({ sphereId, factorId, member, onRemoved, onFailed, disabled = false }) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const { allowed } = useSpherePermission(sphereId, 'edit');
  const [busy, setBusy] = useState(false);

  if (disabled || !allowed || !member.removal) return null;

  const removal = member.removal;
  const memberName = member.sphereName ?? member.sphereId;
  const memberPath = `/spheres/${sphereId}/factors/${factorId}/members/${member.sphereId}`;
  const label =
    removal === 'unlink'
      ? t('statusFactors.roster.unlinkMember', 'Unlink member')
      : t('statusFactors.roster.excludeMember', 'Exclude member');

  // Captured before the DELETE so Undo restores the pin exactly as it was.
  const undo = async () => {
    const graphClient = apiClients?.graphClient;
    if (!graphClient) throw new Error('graph client unavailable');
    await graphClient.post(memberPath, {
      ...(typeof member.influence === 'number' ? { influence: member.influence } : {}),
      sourceFactorId: member.contribution.kind === 'factor' ? (member.contribution.factorId ?? null) : null,
    });
  };

  const remove = async () => {
    const graphClient = apiClients?.graphClient;
    if (!graphClient) return;
    setBusy(true);
    onFailed?.(null);
    try {
      await graphClient.delete(memberPath);
      onRemoved(
        removal === 'unlink'
          ? { kind: 'unlinked', sphereId: member.sphereId, memberName, undo }
          : { kind: 'excluded', sphereId: member.sphereId, memberName, viaKinds: member.sources.map((source) => source.kind) }
      );
    } catch (error) {
      onFailed?.({
        memberSphereId: member.sphereId,
        memberName,
        kind: isForbidden(error) ? 'forbidden' : 'failed',
        retry: () => void remove(),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <IconButtonAtom
      icon={removal === 'unlink' ? <GlyphAtom glyph="lucide:unlink" aria-hidden /> : 'pi pi-ban'}
      aria-label={label}
      disabled={busy}
      onClick={() => void remove()}
      data-testid={`roster-action-${removal}`}
    />
  );
};

export default ActionsCell;
